import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { Modal, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';
import moment from 'moment';

//components
import withPermissions from '~/hoc/withPermissions';
import withLocalization from '~/hoc/withLocalization';
import applicationRouter from '~/hoc/applicationRouter';
import Checkbox from '../../../components/CustomCheckbox/CustomCheckbox';
import ProjectTaskImport from './ProjectTaskImport.js';
import GenericSearch from "../../../components/GenericForm/IntegrationFilter";
import CostUnitStatusFilter, { COST_UNIT_STATUS, isCostUnitTypeExpired, filterByCostUnitStatus } from "../../../components/GenericForm/CostUnitStatusFilter";

//elements
import Button from '../../../components/CustomButton/CustomButton.jsx';

//assets
import CloseIcon from "~/assets/img/modal-close-icon.svg";
import ActivateUser from '../../../assets/img/ActivateUser.svg'
import LoadingSpinner from '../../../elements/LoadingSpinner';

//utils
import { payrollIntegrationOptions } from '../../../utils/constants';
import { getShortDateFormat } from '../../../utils/dateFormat';

const page_size = 10;

const IMPORT_SUCCESS_MESSAGE = "Please assign the projects to members so that members can start recording work hours, etc., and don't forget to include the project address by clicking on the 'Edit' button";


class ProjectImport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showImportModal: true,
            projectToSync: [],
            projectAddIds: [],
            loading: false,
            showResponseText: '',
            nextToken: null,
            previousToken: null,
            pageInfo: null,
            showTaskSyncModal: false,
            projectWithTasks: [],
            projectMapping: [],
            currentFilters: null,
            visibleProjectCount: null,
            isCountLoading: false,
            currentPage: 1, //for visma
            costUnitStatus: COST_UNIT_STATUS.ACTIVE,
            lastUpdated: null
        }
    }

    componentDidMount() {
        const params = {
            first: page_size,
        }
        this.loadData(params);
    }

    getProduct = () => this.props.commonStore.config?.integration_details?.product;

    //visma cost unit functions start ----
    isProjectExpired = (ele) =>
        this.getProduct() === payrollIntegrationOptions.VISMA && isCostUnitTypeExpired(ele);

    getFilteredProjects = () => {
        const { projectToSync, costUnitStatus } = this.state;
        if (this.getProduct() !== payrollIntegrationOptions.VISMA) return projectToSync;
        return filterByCostUnitStatus(projectToSync, costUnitStatus);
    }

    getVisibleProjects = () => {
        const filteredProjects = this.getFilteredProjects();
        if (this.getProduct() !== payrollIntegrationOptions.VISMA) return filteredProjects;
        const start = (this.state.currentPage - 1) * page_size;
        return filteredProjects.slice(start, start + page_size);
    }

    getPagingFlags = () => {
        const { pageInfo, currentPage } = this.state;
        //visma is paged here over the full list, the others are paged by the api and say so themselves
        if (this.getProduct() === payrollIntegrationOptions.VISMA) {
            return {
                hasPreviousPage: currentPage > 1,
                hasNextPage: currentPage * page_size < this.getFilteredProjects().length
            };
        }
        return {
            hasPreviousPage: !!pageInfo?.hasPreviousPage,
            hasNextPage: !!pageInfo?.hasNextPage
        };
    }

    handleStatusFilterChange = (costUnitStatus) => {
        this.setState({ costUnitStatus, currentPage: 1 },
            () => this.selectAllNonSyncedProjects(this.getVisibleProjects()));
    }

    handleRefetchCostUnits = () => {
        this.loadData({ first: page_size, refetch: true });
    }

    //visma cost unit functions end ----

    selectAllNonSyncedProjects = (projectList) => {
        const nonSyncedProjects = projectList.filter(project => !project.isSynced && !this.isProjectExpired(project));
        this.setState({ projectAddIds: nonSyncedProjects })
    }

    async loadData(params) {
        const { projectStore } = this.props
        this.setState({ loading: true })
        const { list, nextCursorToken, previousCursorToken, pageInfo, last_updated } = await projectStore.getAllProjectFromIntegration(params)
        this.setState({ projectToSync: list, nextToken: nextCursorToken, previousToken: previousCursorToken, pageInfo: pageInfo, lastUpdated: last_updated, currentPage: 1, loading: false }, () => {
            this.selectAllNonSyncedProjects(this.getVisibleProjects());
        })
    }

    async handleTaskSync(res2) {
        const { commonStore, t } = this.props;
        const { projectAddIds } = this.state;
        const projects = res2.projectMapping.map(({ projectId, integrationDbId, flexiFieldCode2DbId }) => ({ projectId, projectIntId: integrationDbId ?? null, flexiFieldCode2DbId: flexiFieldCode2DbId ?? null }));
        const res = await this.props.taskStore.saveTaskInBulkFromIntegration({ projects });
        if (res.message) {
            commonStore.addNotification(`${t(res.message)}`, null, 'success');
        }
        this.setState({ loading: false, projectWithTasks: projectAddIds, showImportModal: false, projectMapping: res2.projectMapping })
    }

    importProjectFromIntegration = async () => {
        const { projectStore, commonStore, t } = this.props;
        const { projectAddIds } = this.state;
        const product = this.getProduct();
        if (commonStore.config?.biztype?.data?.allow_multiple_branches) {
            if (commonStore.config?.client?.max_branches_allowed <= projectStore.currentList.total) {
                return commonStore.addNotification(t('You have exceeded project/branch limit, Please Contact Time & Control'), null, 'error');
            }
        }
        const str = t(`Do You want to Import selected Project into TimeAndControl`);
        confirmAlert({
            title: t('Confirm to Import'),
            message: str,
            buttons: [
                {
                    label: t('Yes'),
                    onClick: () => {
                        this.setState({ loading: true })
                        return projectStore.saveProjectFromIntegration({ projects: projectAddIds, product }).then((res2) => {
                            commonStore.addNotification(`${t(res2.message)} ${product}`, null, 'success');
                            projectStore.resetLastListLoadTime();
                            if (product === payrollIntegrationOptions.XLEDGER) {
                                confirmAlert({
                                    title: t('Task Found'),
                                    message: res2.hasTask ? t("Tasks were found linked to the projects you're importing payroll system. Would you like to download these tasks as well?") : t("Would you like to download default tasks for selected projects ?"),
                                    buttons: [
                                        {
                                            label: t('yes'),
                                            onClick: async () => {
                                                await this.handleTaskSync(res2);
                                            }
                                        },
                                        {
                                            label: t('No'),
                                            onClick: () => {
                                                this.setState({ loading: false, showResponseText: t(IMPORT_SUCCESS_MESSAGE) })
                                            }
                                        }
                                    ]
                                })
                            } else {
                                this.setState({ loading: false, showResponseText: t(IMPORT_SUCCESS_MESSAGE) })
                            }
                            return res2;
                        }).catch(error => {
                            console.error({ error });
                            commonStore.addNotification(`${t(error.message || 'Something went wrong')}`, null, 'error');
                            this.setState({ loading: false })
                        });
                    },
                },
                {
                    label: t('No'),
                    onClick: () => { },
                },
            ],
        })
    }

    handleFilterChange = async (filters) => {
        const params = {
            filter: filters,
            first: page_size,
        }
        this.setState({ currentFilters: filters, visibleProjectCount: null }, () => this.loadData(params));
    }

    getProjectKey = (ele) => {
        const product = this.getProduct();
        if (product === payrollIntegrationOptions.TRIPLETEX) return ele.project.id;
        if (product === payrollIntegrationOptions.XLEDGER) return ele.project.node.dbId;
        if (product === payrollIntegrationOptions.VISMA) return ele.project.identifier;
        return null;
    }

    handleCheckbox = (ele, e) => {
        if (e) {
            this.setState({ projectAddIds: [ele, ...this.state.projectAddIds] })
        }
        else {
            const removedKey = this.getProjectKey(ele);
            const updateIds = this.state.projectAddIds.filter(item => this.getProjectKey(item) !== removedKey);
            this.setState({ projectAddIds: updateIds })
        }
    }

    handlePageChange = (isNext) => {
        const { commonStore, t } = this.props;
        const { previousToken, nextToken, currentPage } = this.state;

        //client side pagination for visma
        if (this.getProduct() === payrollIntegrationOptions.VISMA) {
            const nextPage = isNext ? currentPage + 1 : currentPage - 1;
            if (nextPage < 1 || (nextPage - 1) * page_size >= this.getFilteredProjects().length)
                return commonStore.addNotification(t("No data to display"), null, 'warning');
            return this.setState({ currentPage: nextPage },
                () => this.selectAllNonSyncedProjects(this.getVisibleProjects()));
        }

        //xledger pagination is done by the api, so the next and previous tokens are used to fetch the next or previous page
        if ((!nextToken && isNext) || (!previousToken && !isNext))
            return commonStore.addNotification(t("No data to display"), null, 'warning');
        const params = {
            filter: this.state.currentFilters,
        }
        if (isNext) {
            params.first = page_size;
            params.after = nextToken;
        }
        else {
            params.last = page_size;
            params.before = previousToken;
        }
        this.setState({ loading: true, visibleProjectCount: null }, () => this.loadData(params));
    }

    handleTaskImportModal = (value) => {
        this.setState({ showTaskSyncModal: value })
        this.props.projectStore.resetLastListLoadTime();
    }

    isProjectSelected = (project) => {
        const projectKey = this.getProjectKey(project);
        if (projectKey === null) return false;
        return this.state.projectAddIds.some(item => this.getProjectKey(item) === projectKey);
    }

    renderProjectLabel = (ele) => {
        const product = this.getProduct();
        if (product === payrollIntegrationOptions.TRIPLETEX) return ele.project.name;
        if (product === payrollIntegrationOptions.XLEDGER) return ele.project.node.description;
        if (product === payrollIntegrationOptions.VISMA) {
            const { identifier, name, validFrom, validTo } = ele.project;
            if (!validFrom && !validTo) return `${identifier} | ${name}`;
            const shortDateFormat = getShortDateFormat(this.props.commonStore.getDateTimeRules());
            const from = validFrom ? moment(validFrom).format(shortDateFormat) : '';
            const to = validTo ? ` - ${moment(validTo).format(shortDateFormat)}` : '';
            return `${identifier} | ${name} ( ${from}${to} )`;
        }
        return '';
    }

    handleProjectCount = async () => {
        const { currentFilters } = this.state;
        const { commonStore, projectStore, t } = this.props;
        if (!currentFilters || !currentFilters.createdAt_gte || !currentFilters.createdAt_lte) {
            return commonStore.addNotification(t('Please select From Date and To Date to see count of projects'), null, 'warning');
        }

        this.setState({ isCountLoading: true });
        try {
            const { count } = await projectStore.getIntegrationProjectsCount({ filter: currentFilters });

            this.setState({
                visibleProjectCount: count
            });
        } catch (error) {
            console.error(error);
            commonStore.addNotification(t('Failed to fetch project count'), null, 'error');
        } finally {
            this.setState({ isCountLoading: false });
        }
    }

    render() {
        const { t, handleImportModal } = this.props;
        const { loading, showImportModal, showResponseText, costUnitStatus, lastUpdated } = this.state
        const product = this.getProduct();
        const isXledger = product === payrollIntegrationOptions.XLEDGER;
        const isVisma = product === payrollIntegrationOptions.VISMA;
        const hasPaging = isXledger || isVisma;
        const visibleProjects = this.getVisibleProjects();
        const { hasPreviousPage, hasNextPage } = this.getPagingFlags();

        return (
            <div>
                <Modal
                    size="lg"
                    className="modal fade"
                    show={showImportModal}
                    onHide={() => handleImportModal(!this.state.showImportModal)}
                >
                    <Modal.Header>
                        <Modal.Title>{t('Import Projects from')} {product}</Modal.Title>
                        <div className='modal-header__revised__closebtn' onClick={() => handleImportModal(!this.state.showImportModal)}>
                            <img
                                src={CloseIcon}
                                className="cursor-pointer"
                                alt='close_button'
                            />
                        </div>
                    </Modal.Header>
                    <Modal.Body className='mb-2'>
                        {isXledger && <GenericSearch
                            entity="projects"
                            onChange={this.handleFilterChange}
                            fromDateTooltip="Filter projects that started after from date"
                            toDateTooltip="Filter projects that started before to date"
                            displayName="Projects"
                            searchTooltip="Search or filter projects by their name or code. If a project is not in the list, you can still find it by entering its project code and clicking the 'Search' option in the dropdown."
                        />}
                        {loading ?
                            <LoadingSpinner />
                            :
                            <Fragment>
                                {showResponseText === '' ?
                                    <>
                                        {isVisma &&
                                            <CostUnitStatusFilter
                                                value={costUnitStatus}
                                                onChange={this.handleStatusFilterChange}
                                                lastUpdated={lastUpdated}
                                                onRefetch={this.handleRefetchCostUnits}
                                            />
                                        }
                                        <div className='d-flex justify-content-space-between align-items-center mb-2 w-100'>
                                            <div>
                                                <span className='fw-bold font-size-15'>
                                                    {t('Select a project to import')}
                                                </span>
                                                {isXledger && (
                                                    <span className="ms-2">
                                                        {this.state.isCountLoading ? (
                                                            <i className="fa fa-spinner fa-spin text-muted ms-2"></i>
                                                        ) : this.state.visibleProjectCount !== null ? (
                                                            <span className="fw-bold ms-2 color-2550AC">{this.state.visibleProjectCount}</span>
                                                        ) : (
                                                            <OverlayTrigger
                                                                placement="right"
                                                                overlay={
                                                                    <Tooltip id="view-hours-tooltip">
                                                                        {t('Project count')}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <i
                                                                    className="fa fa-eye ms-2 cursor-pointer text-secondary"
                                                                    style={{ fontSize: '0.9em' }}
                                                                    onClick={this.handleProjectCount}
                                                                    role="button"
                                                                    aria-hidden="true"
                                                                />
                                                            </OverlayTrigger>
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                            {hasPaging && <div>
                                                <Button disabled={!hasPreviousPage}
                                                    onClick={() => this.handlePageChange(false)}
                                                >
                                                    <i class="fa fa-arrow-left me-1" aria-hidden="true"></i>
                                                    {t('Prev')}
                                                </Button>
                                                <Button fill wd
                                                    disabled={!hasNextPage}
                                                    style={{ margin: "0 1em" }}
                                                    onClick={() => this.handlePageChange(true)}
                                                >
                                                    {t('Next')}
                                                    <i class="fa fa-arrow-right ms-1" aria-hidden="true"></i>
                                                </Button>
                                            </div>
                                            }
                                        </div>
                                        <div className='projectMemberForm'>
                                            <Table className='w-100 ' style={{ minHeight: '10vh' }}>
                                                <thead>
                                                    <tr className='w-100'>
                                                        <th width={'50%'} style={{ textAlign: 'left' }}>{t('Project')}</th>
                                                        <th width={'50%'}>{t('Synced')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {visibleProjects.length === 0 && <tr><td colSpan={2} className='text-center'>{t('No Data Found')}</td></tr>}
                                                    {visibleProjects.map((ele, idx) => (
                                                        <tr key={idx} className='text-capitalize mt-1'>
                                                            <td width={'50%'} className='' style={{ textAlign: 'left' }}><div className=''>{this.renderProjectLabel(ele)}</div></td>
                                                            <td width={'50%'} className='timelog_array_details'>
                                                                {ele.isSynced ?
                                                                    <img src={ActivateUser} alt='tick' title='Already Synced' />
                                                                    : this.isProjectExpired(ele) ?
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={
                                                                                <Tooltip id={`expired-${idx}`}>
                                                                                    {t('This cost unit type is outside its valid date range in the payroll system. Update the validity dates in the payroll system to import it.')}
                                                                                </Tooltip>
                                                                            }
                                                                        >
                                                                            <span className='text-danger fw-bold font-size-12'>{t('Expired')}</span>
                                                                        </OverlayTrigger>
                                                                        :
                                                                        <Checkbox
                                                                            // isChecked={ele.isSynced || ele?.nowSynced}
                                                                            isChecked={this.isProjectSelected(ele)}
                                                                            onChange={checked => this.handleCheckbox(ele, checked)}
                                                                            disabled={ele.isSynced}
                                                                            wrapperClassName={'bulk-shift-chekbox-style'}
                                                                        />
                                                                }
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                        {!isXledger && <div>
                                            <p className='font-size-12 lh-1 color-2550AC'><span className='fw-bold me-2'>{t('Note')}:</span>{t('All project-related tasks are automatically imported with the projects. To avoid conflicts, please refrain from creating tasks in payroll after the project is imported in TimeAndControl. Instead, create tasks within TimeAndControl and sync them with payroll')}.</p>
                                        </div>}
                                    </>
                                    :
                                    <>
                                        <div className='d-flex justify-content-center align-items-center flex-column'>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="60px" height="60px" viewBox="0 0 1024 1024"><path fill="#2550AC" d="M512 64a448 448 0 1 1 0 896a448 448 0 0 1 0-896zm-55.808 536.384l-99.52-99.584a38.4 38.4 0 1 0-54.336 54.336l126.72 126.72a38.272 38.272 0 0 0 54.336 0l262.4-262.464a38.4 38.4 0 1 0-54.272-54.336L456.192 600.384z"></path></svg>
                                            <div className='w-75'>{showResponseText}</div>
                                        </div>
                                    </>
                                }
                            </Fragment>
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        {showResponseText === '' ?
                            <>
                                <Button fill wd
                                    onClick={this.importProjectFromIntegration}
                                    disabled={!this.state.projectAddIds.length}
                                >
                                    {t('Import Selected Projects')}
                                </Button>
                                <Button wd
                                    onClick={() => handleImportModal(!this.state.showImportModal)}
                                >
                                    {t('Cancel')}
                                </Button>
                            </>
                            :
                            <>
                                <Button wd
                                    onClick={() => handleImportModal(!this.state.showImportModal)}
                                >
                                    {t('OK')}
                                </Button>
                            </>
                        }
                    </Modal.Footer>
                </Modal>
                {
                    this.state.showTaskSyncModal && <ProjectTaskImport
                        handleTaskImportModal={this.handleTaskImportModal}
                        projects={this.state.projectWithTasks}
                        projectMapping={this.state.projectMapping}
                    />
                }
            </div>
        );
    }
}
export default inject(
    'projectStore',
    'userStore',
    'commonStore',
    'taskStore'
)(applicationRouter(withPermissions(withLocalization(observer(ProjectImport)))));
